import AddIcon from '@mui/icons-material/Add';
import { Button, Stack } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { ILineItemSubtype } from 'common/src/types/interfaces';

import {
  addLineItemSubtypeService,
  deleteLineItemSubtypeService,
  getLineItemSubtypesService,
} from 'portal/src/api/LineItemSubtypesService';
import useAuth from 'portal/src/auth/useAuth';
import DataTable from 'portal/src/components/common/DataTable';
import GenericDialog from 'portal/src/components/common/GenericDialog';
import { MESSAGES } from 'portal/src/constants/snackbarMessages';

import LineItemSubTypeForm from './LineItemSubTypeForm';

import useSnackbarHandler from './../../../../../utils/Snackbar/useSnackbarHandler';

const LineItemSubTypeDialogTable = ({
  onClose,
  open,
}: {
  onClose: () => void;
  open: boolean;
}) => {
  const { getUser } = useAuth();
  const { organizationId } = getUser();
  const snackbarHandler = useSnackbarHandler();

  const [showLineItemSubTypeForm, setShowLineItemSubTypeForm] = useState(false);

  const {
    data: lineItemSubtypes,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['lineItemSubtypes'],
    queryFn: async () => await getLineItemSubtypesService(),
    initialData: { data: [] },
  });

  const { mutate: addLineItemSubtype, isPending: isAddLineItemSubtypePending } =
    useMutation({
      mutationKey: ['addLineItemSubtype'],
      mutationFn: async (data: Partial<ILineItemSubtype>) => {
        return await addLineItemSubtypeService(data);
      },
      onSuccess: async () => {
        await refetch();
        setShowLineItemSubTypeForm(false);
        snackbarHandler.success(MESSAGES.LINE_ITEM.SUBTYPE.CREATED);
      },
      onError: () => {
        snackbarHandler.error(MESSAGES.GENERIC.ERROR);
      },
    });

  const handleSaveLineItemSubType = (data: Partial<ILineItemSubtype>) => {
    addLineItemSubtype({ organizationId, ...data });
  };

  const gridColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
    },
  ];

  return (
    <GenericDialog
      title="Line Item Subtypes"
      open={open}
      onClose={onClose}
      dialogActions={
        <Stack direction="row" spacing={2}>
          <Button
            color="info"
            disableElevation
            onClick={onClose}
            variant="outlined"
          >
            Close
          </Button>
        </Stack>
      }
    >
      <DataTable
        actionsConfig={{
          deleteConfig: {
            targetStr: 'line item subtype',
            onDeleteClick: async (id) => {
              await deleteLineItemSubtypeService(id);
              await refetch();
            },
            snackbarMsgKey: 'LINE_ITEM',
          },
        }}
        checkboxSelection={false}
        columns={gridColumns}
        dataFetchError={isError}
        isRowNavigation={false}
        loading={isFetching}
        rows={lineItemSubtypes.data}
        toolbarSlotProps={{
          children: (
            <Button
              color="secondary"
              onClick={() => setShowLineItemSubTypeForm(true)}
              size="small"
              startIcon={<AddIcon />}
              variant="outlined"
            >
              Add Subtype
            </Button>
          ),
          exportFileName: 'line_item_subtypes',
          showFilters: true,
        }}
      />
      {showLineItemSubTypeForm && (
        <LineItemSubTypeForm
          loading={isAddLineItemSubtypePending}
          open={showLineItemSubTypeForm}
          onClose={() => setShowLineItemSubTypeForm(false)}
          onSave={handleSaveLineItemSubType}
        />
      )}
    </GenericDialog>
  );
};

export default LineItemSubTypeDialogTable;
